import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import AdminLayout from "../../components/AdminLayout";
import { api, formatApiErrorDetail } from "../../lib/api";

const EMPTY = { id: "", date: new Date().toISOString().slice(0, 10), outlet: "", title: "", excerpt: "", url: "#", image_url: "" };

export default function AdminPress() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/press");
      setItems(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const existing = items.find((p) => p.id === editing.id);
      if (existing) {
        await api.put(`/admin/press/${editing.id}`, editing);
        toast.success("Press item updated");
      } else {
        await api.post("/admin/press", editing);
        toast.success("Press item created");
      }
      await load();
      setEditing(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this press item?")) return;
    try {
      await api.delete(`/admin/press/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <AdminLayout title="Press & Releases">
      <Toaster theme="dark" position="bottom-right" />
      <div className="flex justify-end mb-6">
        <button data-testid="admin-press-new" onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 text-[12px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-colors">
          <Plus className="w-4 h-4" /> New Press Item
        </button>
      </div>

      <div className="glass overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-press-table">
          <thead className="border-b border-primary/20">
            <tr className="text-left">
              <th className="overline px-6 py-4">Date</th>
              <th className="overline px-6 py-4">Outlet</th>
              <th className="overline px-6 py-4">Title</th>
              <th className="overline px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={4} className="px-6 py-12 text-center text-muted-foreground">No press items yet.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} data-testid={`admin-press-row-${p.id}`} className="border-b border-primary/5 hover:bg-background/30">
                <td className="px-6 py-4 font-mono text-xs text-foreground/60">{p.date}</td>
                <td className="px-6 py-4 text-primary">{p.outlet}</td>
                <td className="px-6 py-4 font-serif text-base max-w-2xl">{p.title}</td>
                <td className="px-6 py-4 text-right">
                  <button data-testid={`admin-press-edit-${p.id}`} onClick={() => setEditing({ ...EMPTY, ...p })} className="p-2 hover:text-primary mr-1"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`admin-press-delete-${p.id}`} onClick={() => remove(p.id)} className="p-2 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-background/90 backdrop-blur-md overflow-y-auto p-6 lg:p-12">
          <div className="max-w-2xl mx-auto glass-strong p-8 lg:p-10">
            <div className="flex items-start justify-between mb-8">
              <div>
                <div className="overline mb-2">— {items.find((p) => p.id === editing.id) ? "Edit" : "Create"}</div>
                <h2 className="font-serif text-3xl">Press item</h2>
              </div>
              <button onClick={() => setEditing(null)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} data-testid="admin-press-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="overline mb-2 block">Date</label>
                <input data-testid="admin-press-input-date" required type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} className="w-full bg-background border border-input px-4 py-3" />
              </div>
              <div>
                <label className="overline mb-2 block">Outlet</label>
                <input data-testid="admin-press-input-outlet" required value={editing.outlet} onChange={(e) => setEditing({ ...editing, outlet: e.target.value })} className="w-full bg-background border border-input px-4 py-3" />
              </div>
              <div className="md:col-span-2">
                <label className="overline mb-2 block">Title</label>
                <input data-testid="admin-press-input-title" required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full bg-background border border-input px-4 py-3" />
              </div>
              <div className="md:col-span-2">
                <label className="overline mb-2 block">Excerpt</label>
                <textarea rows={4} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="w-full bg-background border border-input px-4 py-3 resize-none" />
              </div>
              <div className="md:col-span-2">
                <label className="overline mb-2 block">External URL</label>
                <input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} className="w-full bg-background border border-input px-4 py-3 font-mono text-xs" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-3 pt-4 border-t border-primary/10">
                <button type="button" onClick={() => setEditing(null)} className="px-6 py-3 text-[12px] uppercase tracking-[0.22em] text-foreground/60">Cancel</button>
                <button data-testid="admin-press-save" type="submit" disabled={busy} className="bg-primary text-primary-foreground px-6 py-3 text-[12px] uppercase tracking-[0.22em] disabled:opacity-60">
                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
